import { cx } from "@emotion/css";
import { SlidersHorizontal } from "lucide-react";
import { type ReactNode, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import useKeypress from "react-use-keypress";
import type { SearchCounts, SearchType } from "src/api/search";
import { OutlinedTextBlackButton } from "src/components/Default/LekUI/Button";
import { ScrollShadowWrapper } from "src/components/Default/LekUI/ScrollShadowWrapper";
import Box from "src/components/Default/Main/Box";
import { SearchInput } from "src/components/GlobalSearch/SearchInput";
import isMobile from "src/helpers/isMobile";
import { isLdek, isLek } from "src/helpers/services";

// ponytail: ostatnie liczniki na poziomie modułu — zmiana taba montuje inny widok (inny hook listy),
// więc keepPreviousData z React Query tu nie sięga; trzymamy stare liczby, aż przyjdą nowe
let lastCounts: SearchCounts | undefined;

type SearchHeaderProps = {
	searchValue: string;
	onSearchChange: (value: string) => void;
	activeType: SearchType;
	onTypeChange: (type: SearchType) => void;
	isLoading?: boolean;
	// wynik z filtrami dla aktywnego taba; pozostałe taby liczymy z samej frazy
	count?: number;
	// liczniki tabów z odpowiedzi listy (sama fraza, bez filtrów)
	counts?: SearchCounts;
	tabsActions?: ReactNode;
	activeFiltersCount?: number;
	onOpenFilters?: () => void;
	// taby i chipy zwinięte (scroll w dół)
	collapsed?: boolean;
	// wysokość tabów i chipów — rodzic kompensuje nią zwinięcie, żeby treść pod belką nie skakała
	onDetailsResize?: (height: number) => void;
	children?: ReactNode;
};

export function SearchHeader({
	searchValue,
	onSearchChange,
	activeType,
	onTypeChange,
	isLoading,
	count,
	counts,
	tabsActions,
	activeFiltersCount = 0,
	onOpenFilters,
	collapsed = false,
	onDetailsResize,
	children,
}: SearchHeaderProps) {
	const { t, i18n } = useTranslation();
	const inputRef = useRef<HTMLInputElement>(null);
	const detailsRef = useRef<HTMLDivElement>(null);

	// biome-ignore lint/correctness/useExhaustiveDependencies: observer raz na mount
	useEffect(() => {
		const element = detailsRef.current;
		if (!element || !onDetailsResize) return;
		const observer = new ResizeObserver(() =>
			onDetailsResize(element.offsetHeight),
		);
		observer.observe(element);
		return () => observer.disconnect();
	}, []);
	const types: SearchType[] = [
		"questions",
		...(isLek() || isLdek() ? (["articles"] as const) : []),
		"decks",
	];

	useKeypress(["k", "K"], (event: KeyboardEvent) => {
		if (!(event.metaKey || event.ctrlKey)) return;
		event.preventDefault();
		inputRef.current?.focus();
	});

	const getCount = (type: SearchType) =>
		type === activeType && count !== undefined
			? count
			: (counts ?? lastCounts)?.[type];
	if (counts) lastCounts = counts;

	return (
		<div className="search-header">
			<div className="search-header__row">
				<SearchInput
					className="search-header__input"
					inputRef={inputRef}
					value={searchValue}
					onChange={onSearchChange}
					onClear={() => {
						onSearchChange("");
						inputRef.current?.focus();
					}}
					isLoading={isLoading}
					showShortcut={!isMobile()}
					placeholder={t(`globalSearch.${activeType}`)}
				/>
				{onOpenFilters && (
					<OutlinedTextBlackButton
						className="search-header__filters"
						onClick={onOpenFilters}
						aria-haspopup="dialog"
						data-test-id="search-filters-button"
					>
						<SlidersHorizontal className="icon-small" />
						<span className="search-header__filters-label">
							{t("search.filters.title", "Filtry")}
						</span>
						{!!activeFiltersCount && (
							<span className="count-badge">{activeFiltersCount}</span>
						)}
					</OutlinedTextBlackButton>
				)}
			</div>
			{/* taby i chipy chowają się przy scrollu w dół, wracają przy scrollu w górę */}
			<div
				className={cx("collapsible", { "collapsible--collapsed": collapsed })}
			>
				<div>
					<div ref={detailsRef} className="search-header__details">
						<Box
							className="border-none box-shadow-none p-0"
							headerClassName="mb-0"
							tabsClassName="tabs-scroll-shadow"
							TabsWrapper={ScrollShadowWrapper}
							additionalTabsActions={
								tabsActions ? () => tabsActions : undefined
							}
							tabs={types.map((type) => {
								const typeCount = getCount(type);
								return (
									<>
										{t(`globalSearch.tabs.${type}`)}
										{typeCount !== undefined && (
											<span className="tab-count">
												{typeCount.toLocaleString(i18n.language)}
											</span>
										)}
									</>
								);
							})}
							selectedTab={types.indexOf(activeType)}
							onTabsChange={(index) => onTypeChange(types[index])}
							tabTestIds={types.map((type) => `search-type-${type}`)}
						/>
						{children}
					</div>
				</div>
			</div>
		</div>
	);
}
